import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd'
import {
    validateThemeSections,
    type ThemeManifest,
    type ThemePageKey,
    type ThemePartKey,
    type ThemeSectionInstance,
} from '@mediardex/contracts'
import { useState } from 'react'
import { MdAdd, MdDelete, MdDragIndicator, MdPalette, MdVisibility, MdVisibilityOff } from 'react-icons/md'

import { useAnchoredDropdown } from '@/components/use-anchored-dropdown'

/**
 * O que está aberto no painel da direita: as configurações da página (na inicial e na
 * barra lateral, que não têm, vale a parte "Estilo"), uma parte da loja ou uma seção.
 */
export type ThemeEditorSelection =
    | { kind: 'settings' }
    | { kind: 'part'; part: ThemePartKey }
    | { kind: 'section'; id: string }

interface SectionListProps {
    manifest: ThemeManifest
    page: ThemePageKey
    sections: ThemeSectionInstance[]
    selection: ThemeEditorSelection
    onSelect: (selection: ThemeEditorSelection) => void
    onChange: (sections: ThemeSectionInstance[]) => void
    /** Rótulo do botão de configurações da página; sem ele, o botão não aparece. */
    settingsLabel?: string
}

/** Identificador estável de seção nova, no formato que o manifesto aceita. */
function newSectionId(type: string): string {
    return `${type}-${Math.random().toString(36).slice(2, 8)}`.slice(0, 40)
}

export default function SectionList({
    manifest,
    page,
    sections,
    selection,
    onSelect,
    onChange,
    settingsLabel,
}: SectionListProps) {
    const pageDef = manifest.pages[page]
    const addable = (pageDef?.allowedSections ?? []).filter((type) => {
        const def = manifest.sections[type]
        return def && !(def.unique && sections.some((s) => s.type === type))
    })

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination || result.destination.index === result.source.index) return
        const next = [...sections]
        const [moved] = next.splice(result.source.index, 1)
        next.splice(result.destination.index, 0, moved)
        onChange(next)
    }

    // O menu sai do fluxo da coluna rolável (portal) e abre para cima quando falta
    // espaço embaixo; dentro do overflow, o dropdown do DaisyUI saía cortado.
    const [addOpen, setAddOpen] = useState(false)
    const { anchorRef, renderPanel } = useAnchoredDropdown(addOpen, () => setAddOpen(false))

    const addSection = (type: string) => {
        setAddOpen(false)
        const id = newSectionId(type)
        // Já com os padrões do manifesto (textos e itens de exemplo), para o lojista
        // ver no editor o mesmo que a loja vai mostrar.
        const [section] = validateThemeSections(manifest, page, [{ id, type, settings: {} }]).sections
        onChange([...sections, section ?? { id, type, settings: {} }])
        onSelect({ kind: 'section', id })
    }

    return (
        <div className="flex flex-col gap-4">
            {settingsLabel && (
                <button
                    type="button"
                    className={`btn btn-sm justify-start gap-2 ${selection.kind === 'settings' ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => onSelect({ kind: 'settings' })}
                >
                    <MdPalette className="text-lg" />
                    {settingsLabel}
                </button>
            )}

            <div className="flex flex-col gap-2">
                <span className="px-1 text-xs font-semibold uppercase tracking-wide text-base-content/60">
                    Seções
                </span>

                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId={`theme-sections-${page}`}>
                        {(provided) => (
                            <ul ref={provided.innerRef} {...provided.droppableProps} className="flex flex-col gap-1">
                                {sections.map((section, index) => {
                                    const def = manifest.sections[section.type]
                                    const selected = selection.kind === 'section' && selection.id === section.id
                                    return (
                                        <Draggable key={section.id} draggableId={section.id} index={index}>
                                            {(drag) => (
                                                <li
                                                    ref={drag.innerRef}
                                                    {...drag.draggableProps}
                                                    className={`group flex items-center gap-1 rounded-lg border px-2 py-1.5 ${selected ? 'border-primary bg-primary/10' : 'border-transparent hover:bg-base-200'}`}
                                                >
                                                    <span {...drag.dragHandleProps} className="cursor-grab text-base-content/40" title="Arrastar">
                                                        <MdDragIndicator />
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className={`flex-1 truncate text-left text-sm ${section.hidden ? 'text-base-content/40 line-through' : ''}`}
                                                        onClick={() => onSelect({ kind: 'section', id: section.id })}
                                                    >
                                                        {def?.label ?? section.type}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-xs"
                                                        title={section.hidden ? 'Mostrar' : 'Esconder'}
                                                        onClick={() =>
                                                            onChange(
                                                                sections.map((s) =>
                                                                    s.id === section.id ? { ...s, hidden: !s.hidden } : s,
                                                                ),
                                                            )
                                                        }
                                                    >
                                                        {section.hidden ? <MdVisibilityOff /> : <MdVisibility />}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-xs text-error opacity-0 group-hover:opacity-100"
                                                        title="Remover"
                                                        onClick={() => {
                                                            onChange(sections.filter((s) => s.id !== section.id))
                                                            if (selected) onSelect({ kind: 'settings' })
                                                        }}
                                                    >
                                                        <MdDelete />
                                                    </button>
                                                </li>
                                            )}
                                        </Draggable>
                                    )
                                })}
                                {provided.placeholder}
                            </ul>
                        )}
                    </Droppable>
                </DragDropContext>

                {addable.length > 0 && (
                    <div ref={anchorRef} className="w-full">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline w-full"
                            onClick={() => setAddOpen((v) => !v)}
                        >
                            <MdAdd /> Adicionar seção
                        </button>
                        {renderPanel(
                            <ul className="menu w-full p-2">
                                {addable.map((type) => (
                                    <li key={type}>
                                        <button type="button" onClick={() => addSection(type)}>
                                            {manifest.sections[type].label}
                                        </button>
                                    </li>
                                ))}
                            </ul>,
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
